import { useMemo, useState } from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import styles from './AreaForm.module.scss';
import { ColorPicker } from '../ColorPicker';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { WKTLoader } from '@loaders.gl/wkt';
import { useCreateAreaMutation, useUpdateAreaMutation } from 'app/api/marine-infrastructure';
import { Link } from 'react-router-dom';
import { Icon } from '../Icon';
import { useAlertHook } from 'app/hooks/useAlertHook';
import * as Yup from 'yup';
import { AreaCardData, AreaCreationParams, AreaInfo, AreaUpdateParams } from 'ais-facade-api/api';
import { parseSync } from '@loaders.gl/core';

const options = [
  '#FFFFFF',
  '#999999',
  '#000000',
  '#FFEE55',
  '#FEBC11',
  '#EF1010',
  '#73E916',
  '#16CFE9',
  '#FA00FF',
  '#3AB600',
  '#136AEC',
  '#C600B3',
];
export const AreaForm = ({
  area,
  mode,
  onClose,
  onSuccess,
}: {
  area?: AreaCardData;
  mode: 'EDIT' | 'CREATE';
  onClose: () => void;
  onSuccess: (area: AreaInfo) => void;
}) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();

  const [color, setColorValue] = useState('#73E916');

  const [updateArea] = useUpdateAreaMutation();
  const [createArea] = useCreateAreaMutation();

  const initialFormValues = useMemo(() => {
    if (mode === 'EDIT') {
      return {
        name: area?.name,
        geometry: area?.geometry,
        color: area?.color,
      };
    } else {
      return {
        name: '',
        geometry: '',
        color: '#73E916',
      };
    }
  }, [area, mode]);

  const handleCreate = (values: Partial<AreaCreationParams>, { resetForm }: FormikHelpers<any>) => {
    if (values.name && values.geometry) {
      createArea({
        value: {
          name: values.name,
          geometry: values.geometry,
          color: values.color,
        },
      })
        .unwrap()
        .then((res) => {
          onSuccess({
            code: res.code,
            name: values.name,
            type: 'USER',
            color: values.color,
          });
          resetForm();

          onClose();
        })
        .catch((error) => addAlert(error));
    }
  };

  const handleUpdate = (values: Partial<AreaUpdateParams>, { resetForm }: FormikHelpers<any>) => {
    if (area?.code && values.name && values.geometry) {
      const value = {
        name: values.name,
        geometry: values.geometry,
        color: values.color,
      };

      updateArea({
        code: area?.code,
        body: {
          value,
        },
      })
        .unwrap()
        .then(() => {
          onSuccess({
            code: area.code,
            name: values.name,
            type: 'USER',
            color: values.color,
          });
          resetForm();

          onClose();
        })
        .catch((error) => addAlert(error));
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('GeofenceNamePlaceholder')).max(256, t('IncorrectGeofenceNameLenghtError')),
    geometry: Yup.string()
      .required(t('GeofenceWktPlaceholder'))
      .test(`test-wkt-string`, 'error', function (value) {
        const { path, createError } = this;
        const joinedString = value?.split('\n').join('');

        if (joinedString && parseSync(joinedString, WKTLoader)) {
          return true;
        } else {
          return createError({ path, message: t('IncorrectGeofenceWKTError') });
        }

      }),
    color: Yup.string().required(),
  });

  return (
    <div>
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={mode === 'EDIT' ? handleUpdate : handleCreate}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, setFieldTouched, isValid, dirty, touched, errors, resetForm }) => {
          const selectedColor = values?.color || color;
          const isSubmitButtonDisabled = !isValid || !dirty;

          return (
            <div className={styles.form_container}>
              <Form>
                <div className={styles.container}>
                  <div className={styles.nameField}>
                    <label htmlFor="name">{`${t('GeofenceName')}:`}</label>
                    <Field id="name" name="name" placeholder={t('GeofenceNamePlaceholder')} />
                  </div>
                  <div className={styles.wktField}>
                    <div className={styles.wktLabel}>
                      <label htmlFor="geometry">{`${t('GeofenceWktLabel')}:`}</label>
                      <>
                        <Link
                          className={styles.link}
                          title={t('Editor')}
                          to="https://arthur-e.github.io/Wicket/sandbox-gmaps3.html"
                          target="_blank"
                        >
                          {t('Editor')}
                          <Icon name="away" size="16" />
                        </Link>
                      </>
                    </div>

                    <Field
                      id="geometry"
                      name="geometry"
                      as="textarea"
                      onInput={(e: any) => {
                        e.target.parentNode.dataset.replicatedValue = e.target.value;
                      }}
                      placeholder={t('GeofenceWktPlaceholder')}
                    />
                  </div>
                  <div className={styles.colorField}>
                    <label htmlFor="color">{`${t('Color')}:`}</label>
                    <div className={styles.dots} />

                    <ColorPicker
                      selected={selectedColor}
                      id="color"
                      onChange={(value) => {
                        setColorValue(value);
                        setFieldValue(`color`, value);
                        setFieldTouched('color', true);
                      }}
                      options={options}
                    />
                  </div>
                  <div className={styles.error}>
                    {(touched.name && errors.name) || (touched.geometry && errors.geometry) || ' '}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button
                    disabled={isSubmitButtonDisabled}
                    className={styles.action_button}
                    label={t('ButtonLabelSave')}
                    variant="general"
                    type="submit"
                  />

                  <Button
                    className={styles.action_button}
                    type="button"
                    label={t('ButtonLabelDiscard')}
                    variant="general"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                  />
                </div>
              </Form>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};
